import { useState } from "react";
import { Play } from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "@/i18n";
import { VideoModal } from "@/components/VideoModal";

// Hero background image - New image uploaded by user
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_6755e027-e15f-4d08-a33d-914c47bd2f3e/artifacts/slji7g3g_magnific_cambia-la-paleta-de-color_2975555788.jpeg";

// Showreel URLs
const SHOWREEL_CREATIVO_URL = "https://youtu.be/QiOJiAdgChw";
const SHOWREEL_IA_EDITS_URL = "https://youtu.be/vbeF2Ggc5LU";

export const HeroSection = () => {
  const { t } = useLanguage();
  const [modalOpen, setModalOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  
  const openVideoModal = (videoUrl) => {
    setActiveVideo(videoUrl);
    setModalOpen(true);
  };
  
  const closeVideoModal = () => {
    setModalOpen(false);
    setActiveVideo(null);
  };
  
  return (
    <section className="relative w-full min-h-screen flex items-center justify-center">
      {/* Background Image with preserved sharpness */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="Spurna Creativa - Visión cinematográfica"
          className="w-full h-full object-cover object-center"
          loading="eager"
          fetchPriority="high"
          style={{ imageRendering: 'auto' }}
        />
        
        {/* Subtle vignette overlay - preserves image detail */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 30%, hsl(30 10% 5% / 0.4) 100%)'
          }}
        />
        
        {/* Subtle gradient for text area readability - centered on water area */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 70% 25% at 50% 55%, hsl(30 10% 5% / 0.5) 0%, transparent 70%)'
          }}
        />
        
        {/* Subtle film grain texture overlay */}
        <div className="absolute inset-0 grain-overlay opacity-[0.03]" />
      </div>

      {/* Content Container - Positioned in center-lower area (water zone) */}
      {/* Using margin-top to push content down into the water area, away from cathedral */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 mt-[15vh] sm:mt-[18vh] lg:mt-[20vh]">
        {/* SINGLE CONTENT GROUP: Headline + CTAs with tight spacing */}
        <div className="flex flex-col items-center text-center">
          {/* Main Headline - ONE LINE on desktop/tablet, wrap allowed on mobile */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="font-serif font-light leading-snug tracking-wide px-2"
            style={{ 
              textShadow: '0 2px 30px hsl(30 10% 5% / 0.6)',
              fontSize: 'clamp(1.05rem, 3vw, 2.2rem)',
            }}
          >
            <span className="text-foreground">{t.home.headline1}</span>
            {' '}
            <span className="text-primary">{t.home.headline2}</span>
          </motion.h1>

          {/* TWO CTA Buttons - Tight spacing, stacked vertically, grouped with headline */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: [0.4, 0, 0.2, 1] }}
            className="mt-6 sm:mt-7 lg:mt-8 flex flex-col items-center gap-3"
          >
            {/* CTA 1: Reel Narrativo */}
            <button
              onClick={() => openVideoModal(SHOWREEL_CREATIVO_URL)}
              className="btn-hero-reel group"
              aria-label={t.home.showreelCreativo}
              data-testid="showreel-creativo-btn"
            >
              <Play className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
              <span>{t.home.showreelCreativo}</span>
            </button>

            {/* CTA 2: Reel Publicitario */}
            <button
              onClick={() => openVideoModal(SHOWREEL_IA_EDITS_URL)}
              className="btn-hero-reel group"
              aria-label={t.home.showreelIA}
              data-testid="showreel-ia-btn"
            >
              <Play className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
              <span>{t.home.showreelIA}</span>
            </button>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator - at very bottom */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 0.6 }}
        className="absolute bottom-4 left-1/2 -translate-x-1/2"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-[10px] tracking-[0.25em] uppercase text-foreground/30">{t.home.scroll}</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            className="w-px h-6 bg-gradient-to-b from-foreground/30 to-transparent"
          />
        </div>
      </motion.div>
      
      {/* Video Modal */}
      <VideoModal
        isOpen={modalOpen}
        onClose={closeVideoModal}
        videoUrl={activeVideo}
      />
    </section>
  );
};
